<template>
  <div>
    <v-card class="pa-5">
      <v-card-text>
        <v-card-title class="d-flex align-center">
          <h4>{{ $t('reports.inspectionPointsWithDamages') }}</h4>
          <v-spacer></v-spacer>
          
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn @click="filterData()" v-on="on" v-bind="attrs" depressed color="primary" class="mr-2" min-width="40">
                  <v-icon small>fas fa-search</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('general.search') }}</span>
            </v-tooltip>
            
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn @click="resetFilter" v-on="on" v-bind="attrs" depressed color="primary" class="mr-2" min-width="40">
                  <v-icon small>fa-solid fa-eraser</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('general.resetFilter') }}</span>
            </v-tooltip>
            
            <v-btn v-if="reportData.length > 0" @click="download" :loading="loadingButton" color="primary" depressed>
              {{ $t('reports.downloadReport') }}
              <v-icon small class="ml-2">fas fa-cloud-download-alt</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form v-model="valid" lazy-validation ref="form">
            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  clearable
                  outlined
                  dense
                  :items="reports"
                  item-text="name"
                  item-value="id"
                  v-model="filter.checklist_id"
                  label="Checklist"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  clearable
                  outlined
                  dense
                  :items="unitTypes"
                  item-text="alias"
                  item-value="id"
                  v-model="filter.unit_type_id"
                  :label="$t('general.unitType')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  clearable
                  outlined
                  dense
                  :items="sites"
                  item-text="name"
                  item-value="id"
                  v-model="filter.site_id"
                  :label="$t('general.yard')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-autocomplete
                  clearable
                  outlined
                  dense
                  :items="damageLevels"
                  item-text="name"
                  item-value="id"
                  v-model="filter.damage_level"
                  :label="$t('monitoring.levelDamage')"
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col v-if="filter.dateRange" cols="12" sm="4" md="3">
                <v-menu v-model="menu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filter.date_start"
                      :label="$t('general.startDate')"
                      outlined
                      dense
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    no-title
                    color="primary"
                    v-model="filter.date_start"
                    @input="handleDateStartSelection"
                    :allowed-dates="allowedStartDates"
                    :day-style="getDayStyle"
                  />
                </v-menu>
              </v-col>
              <v-col v-if="filter.dateRange" cols="12" sm="4" md="3">
                <v-menu v-model="menu2" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filter.date_end"
                      :label="$t('general.endDate')"
                      outlined
                      dense
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    no-title
                    color="primary"
                    v-model="filter.date_end"
                    @input="menu2 = false"
                    :allowed-dates="allowedEndDates"
                    :day-style="getDayStyle"
                  />
                </v-menu>
              </v-col>
              <v-col cols="3">
                <v-checkbox 
                  v-model="filter.dateRange" 
                  :label="$t('general.dateRange')" 
                  hide-details 
                  class="mt-0"
                  color="primary"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-overlay absolute opacity="0" :value="loadingTable"/>
        <v-data-table
          :loading="loadingTable"
          :loading-text="$t('general.loading')"
          :headers="headers"
          :items="reportData"
          :single-expand="false"
          :server-items-length="optionsDataTable.totalItems"
          :options.sync="optionsDataTable"
          :footer-props="{ 'items-per-page-options': [50, 70, 100] }"
          @update:page="get"
          @update:items-per-page="get"
          @update:sort-by="get"
          @update:sort-desc="get"
          item-key="id"
          class="elevation-1"
        >
          <template v-slot:item.name="{ item }">
            {{ item.name || 'N/A' }}
          </template>
          <template v-slot:item.total="{ item }">
            <strong>{{ item.total || 0 }}</strong>
          </template>
          <template v-slot:item.soft="{ item }">
            {{ item.soft || 0 }}
          </template>
          <template v-slot:item.moderate="{ item }">
            {{ item.moderate || 0 }}
          </template>
          <template v-slot:item.hard="{ item }">
            {{ item.hard || 0 }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      valid: true,
      loadingTable: false,
      loadingButton: false,
      menu: false,
      menu2: false,
      optionsDataTable: {
        itemsPerPage: 15,
        page: 1,
        total: 0,
        sortBy: ['hard'],
        sortDesc: [true]
      },
      filter: {
        checklist_id: null,
        unit_type_id: null,
        site_id: null,
        damage_level: null,
        dateRange: true,
        date_start: this.returnStartDate(),
        date_end: this.returnEndDate()
      },
      reportData: [],
      sites: [],
      unitTypes: [],
      reports: []
    }
  },
  watch: {
    'filter.dateRange'(val) {
      if (!val) {
        this.filter.date_start = null
        this.filter.date_end = null
      } else {
        this.filter.date_start = this.returnStartDate()
        this.filter.date_end = this.returnEndDate()
      }
    }
  },
  computed: {
    damageLevels() {
      return [
        { id: 'SOFT', name: this.$t('inspections.slight') },
        { id: 'MODERATE', name: this.$t('inspections.moderate') },
        { id: 'HARD', name: this.$t('inspections.high') }
      ]
    },
    headers() {
      return [
        {
          text: this.$t('monitoring.question'),
          align: 'start',
          value: 'name',
          sortable: true
        },
        {
          text: this.$t('general.totalDamages'),
          align: 'center',
          value: 'total',
          sortable: true
        },
        {
          text: this.$t('general.minorDamage'),
          align: 'center',
          value: 'soft',
          sortable: false
        },
        {
          text: this.$t('general.moderateDamage'),
          align: 'center',
          value: 'moderate',
          sortable: false
        },
        {
          text: this.$t('general.highDamage'),
          align: 'center',
          value: 'hard',
          sortable: false
        }
      ]
    }
  },
  created() {
    this.$store.dispatch('sites/get', { event: { context: this, item: { perPage: -1, page: 1 } } })
    this.$store.dispatch('unitTypes/getAll', { event: { context: this } })
    this.$store.dispatch('reports/getQuestionnaires', { event: { context: this } })

    this.get()
  },
  methods: {
    returnStartDate() {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().substr(0, 10)
    },
    returnEndDate() {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().substr(0, 10)
    },
    resetFilter() {
      this.filter = {
        checklist_id: null,
        unit_type_id: null,
        site_id: null,
        damage_level: null,
        dateRange: true,
        date_start: this.returnStartDate(),
        date_end: this.returnEndDate()
      }

      if (this.optionsDataTable.page === 1) return this.get()
      this.optionsDataTable.page = 1
    },
    filterData() {
      if (!this.$refs.form.validate()) return this.showNotification(this.$t('general.errorFilterMessage'), 'warn', -1)
      if (this.optionsDataTable.page === 1) return this.get()
      this.optionsDataTable.page = 1
    },
    download() {
      if (!this.$refs.form.validate()) return this.showNotification(this.$t('general.errorFilterMessage'), 'warn', -1)

      this.loadingButton = true
      
      this.$store.dispatch('reports/getInspectionPointsWithDamagesDoc', {
        event: {
          context: this,
          params: {
            ord: this.optionsDataTable.sortDesc && this.optionsDataTable.sortDesc[0] ? 'DESC' : 'ASC',
            perPage: this.optionsDataTable.itemsPerPage,
            page: this.optionsDataTable.page,
            type: 'download',
            date_start: this.filter.date_start === null ? undefined : this.filter.date_start,
            date_end: this.filter.date_end === null ? undefined : this.filter.date_end,
            site_id: this.filter.site_id,
            questionnaire_id: this.filter.checklist_id,
            unit_type_id: this.filter.unit_type_id,
            damage_level: this.filter.damage_level,
            sortKey: this.optionsDataTable.sortBy.length > 0 ? this.optionsDataTable.sortBy[0] : 'hard'
          }
        }
      })
    },
    get() {
      this.loadingTable = true
      this.$store.dispatch('reports/getInspectionPointsWithDamages', {
        event: {
          context: this,
          params: {
            date_start: this.filter.date_start === null ? undefined : this.filter.date_start,
            date_end: this.filter.date_end === null ? undefined : this.filter.date_end,
            site_id: this.filter.site_id,
            questionnaire_id: this.filter.checklist_id,
            unit_type_id: this.filter.unit_type_id,
            damage_level: this.filter.damage_level,
            perPage: this.optionsDataTable.itemsPerPage,
            page: this.optionsDataTable.page,
            type: 'json',
            sortKey: (this.optionsDataTable.sortBy && this.optionsDataTable.sortBy.length > 0) ? this.optionsDataTable.sortBy[0] : 'hard',
            ord: this.optionsDataTable.sortDesc && this.optionsDataTable.sortDesc[0] ? 'DESC' : 'ASC'
          }
        }
      })
    },
    handleDateStartSelection(date) {
      this.filter.date_start = date;
      this.updateDateEnd();
      this.menu = false;
    },
    allowedStartDates() {
      return true;
    },
    allowedEndDates(date) {
      if (!this.filter.date_start) return true;

      const startDate = new Date(this.filter.date_start);
      const endDateLimit = new Date(startDate);
      endDateLimit.setDate(startDate.getDate() + 29);

      return new Date(date) >= startDate && new Date(date) <= endDateLimit;
    },
    updateDateEnd() {
      if (this.filter.date_start) {
        const startDate = new Date(this.filter.date_start);
        const endDateLimit = new Date(startDate);
        endDateLimit.setDate(startDate.getDate() + 29);

        this.filter.date_end = endDateLimit.toISOString().substr(0, 10);
      }
    },
    getDayStyle(date) {
      if ((this.filter.date_start && this.allowedEndDates(date)) || (!this.filter.date_start && this.allowedStartDates(date))) {
        return null;
      }
      return { backgroundColor: 'rgba(0, 0, 0, 0.1)', color: 'gray' };
    }
  }
}
</script>